<template>
    <div class="space-y-8">
        <Card class="w-full">
            <CardContent class="p-0">
                <!-- Cover Photo -->
                <div class="h-48 bg-gray-400 relative">
                    <input v-if="isOwnProfile" class="hidden"
                        accept="image/png, image/jpg, image/jpeg, image/gif, image/avif" type="file"
                        @change="onAvatarChange" id="avatar">
                        <input v-if="isOwnProfile" class="hidden"
                            accept="image/png, image/jpg, image/jpeg, image/gif, image/avif" type="file"
                            @change="onBannerChange" id="banner">
                            <label for="banner"
                                class="absolute top-8 -translate-y-1/2 -translate-x-1/2 left-1/2 bg-muted rounded-full p-4 cursor-pointer group"
                                v-if="isOwnProfile">
                                <div class="relative">
                                    <ImageUp v-if="
                                        !banner?.bucketLocation &&
                                        !bannerPreview
                                    " class="w-4 h-4" />
                                    <Pencil v-else class="w-4 h-4" />

                                    <div
                                        class="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-48 p-2 bg-black/80 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 text-center">
                                        Banner: JPG, JPEG, PNG, AVIF (Máx 3MB)<br>
                                        Recomendado: 1200x480px
                                    </div>
                                </div>
                            </label>
                            <img v-if="banner || bannerPreview"
                                :src="bannerPreview ? bannerPreview : banner!.bucketLocation" alt="Cover Photo"
                                class="w-full h-full object-cover" />
                            <div class="flex items-end justify-end gap-4 flex-col">
                                <label for="avatar" class="group">
                                    <Avatar
                                        class="absolute bottom-0 left-4 transform translate-y-1/2 border-4 border-white cursor-pointer"
                                        size="lg">
                                        <AvatarImage v-if="avatar?.bucketLocation || avatarPreview"
                                            :src="avatarPreview ? avatarPreview : avatar!.bucketLocation" />
                                        <AvatarFallback v-if="props.user">{{
                                            props.user.name.charAt(0).toUpperCase()
                                            }}</AvatarFallback>
                                        <AvatarFallback v-else class="animate-pulse bg-zinc-800" />

                                        <div
                                            class="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-full">
                                            <Pencil class="w-6 h-6 text-white" />
                                        </div>
                                    </Avatar>
                                    <div
                                        class="absolute bottom-[-40px] left-4 w-32 p-1 bg-black/80 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 text-center">
                                        Avatar: JPG, JPEG, PNG, AVIF (Máx 3MB)<br>
                                        Recomendado: 400x400px
                                    </div>
                                </label>

                                <div class="flex gap-2 flex-row flex-wrap mr-3" v-if="isOwnProfile">
                                    <Button @click="openEditModal">Editar perfil</Button>
                                    <Button v-if="!!profilePayload.avatar || !!profilePayload.banner"
                                        @click="updateProfile" class="bg-emerald-500 text-white hover:bg-emerald-600">
                                        <Loader2 v-if="isUpdatingProfile" class="w-4 h-4 mr-2 animate-spin" />
                                        Salvar alterações
                                    </Button>
                                </div>


                                <TooltipProvider>
                                    <Tooltip :delay-duration="100">
                                        <TooltipTrigger asChild>
                                            <div :class="{ 'cursor-not-allowed': !hasActiveParticipation }">
                                                <div class="flex gap-2 flex-row flex-wrap mr-3">
                                                    <Button :disabled="!hasActiveParticipation"
                                                        v-if="props.user && !isOwnProfile"
                                                        @click="isSendMessageDialogOpen = true">Enviar mensagem</Button>
                                                </div>
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent v-if="!hasActiveParticipation">
                                            <p>Ação bloqueada! É necessário estar participando de um torneio.</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                </div>

                <!-- Profile Info -->
                <div class="pt-16 px-4 mt-4">
                    <div class="flex justify-between items-start mb-4">
                        <div>
                            <div v-if="props.user" class="flex items-bottom gap-2">
                                <h2 class="text-[1.1rem] font-bold">{{ props.user.name }}</h2>
                                <Handshake v-if="props.user.isTrader" class="text-green-500 w-7 h-7 relative"
                                    title="Trader Verificado" />
                            </div>
                            <div v-else class="h-7 w-48 bg-zinc-800 rounded animate-pulse mb-2"></div>

                            <p v-if="props.user" class="mb-4 text-sm mt-4 text-zinc-400">{{ props.user.description }}
                            </p>

                            <!-- Stats -->
                            <div class="flex items-center gap-8 mb-6">
                                <div class="text-center">
                                    <p class="font-semibold mb-2 text-sm">Publicações</p>
                                    <p v-if="topics" class="font-semibold w-[38px] h-[38px] text-center min-w-full">{{
                                        topics?.pagination.total }}</p>
                                    <div v-else class="h-5 w-8 bg-zinc-800 rounded animate-pulse mx-auto"></div>
                                </div>
                            </div>
                        </div>
                        <div class="flex items-end justify-end gap-4 flex-col">
                            <div class="lg:flex gap-4">
                                <!-- Achievements section -->
                                <div
                                    class="flex flex-col items-start justify-start min-h-[70px] w-[200px] xl:w-[260px] flex-wrap">
                                    <h3 class="font-semibold mb-2 text-sm">Troféus</h3>
                                    <div class="flex flex-wrap gap-2" v-if="props.user">
                                        <TooltipProvider v-if="trophies.length > 0">
                                            <Tooltip v-for="achievement in trophies" :key="achievement.id"
                                                :delay-duration="200">
                                                <TooltipTrigger>
                                                    <img :src="achievement.icon" style="height: 7rem;" />
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <div class="flex justify-center items-center"><img
                                                            :src="achievement.icon" class="h-20" /></div>
                                                    <p class="font-semibold">{{ achievement.title }}</p>
                                                    <p>{{ achievement.description }}</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                        <p v-else class="text-xs text-zinc-500 italic">Nenhum troféu ainda</p>
                                    </div>
                                    <div v-else class="flex gap-2 animate-pulse mt-1">
                                        <div v-for="i in 3" :key="i" class="w-12 h-12 bg-zinc-800 rounded"></div>
                                    </div>
                                </div>
                                <!-- Achievements section -->
                                <div
                                    class="flex flex-col items-start justify-start min-h-[70px] w-[200px] xl:w-[260px] flex-wrap">
                                    <h3 class="font-semibold mb-2 text-sm">Conquistas</h3>
                                    <div class="flex flex-wrap gap-2" v-if="props.user">
                                        <TooltipProvider v-if="achievements.length > 0">
                                            <Tooltip v-for="achievement in achievements" :key="achievement.id"
                                                :delay-duration="200">
                                                <TooltipTrigger>
                                                    <Badge variant="secondary" class="p-2">
                                                        <img :src="achievement.icon" class="h-10" />
                                                    </Badge>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <div class="flex justify-center items-center"><img
                                                            :src="achievement.icon" class="h-20 text-center" /></div>
                                                    <p class="font-semibold">{{ achievement.title }}</p>
                                                    <p>{{ achievement.description }}</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                        <p v-else class="text-xs text-zinc-500 italic">Nenhuma conquista ainda</p>
                                    </div>
                                    <div v-else class="flex gap-2 animate-pulse mt-1">
                                        <div v-for="i in 5" :key="i" class="w-8 h-8 bg-zinc-800 rounded"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>

        <Dialog v-model:open="isSendMessageDialogOpen">
            <DialogContent class="w-full max-w-[500px] grid-rows-[auto_minmax(0,1fr)_auto]">
                <DialogHeader>
                    <DialogTitle v-if="hasActiveParticipation">Enviar mensagem</DialogTitle>
                </DialogHeader>
                <Textarea id="message" placeholder="ex: Olá..." v-model="message" />
                <Button :disabled="isSendingMessage || !message" @click="() => sendMessage(message, Number(profileId), () => {
                    toast({
                        title: 'Sucesso',
                        description: 'Mensagem enviada',
                        class: 'bg-green-500 text-white',
                        duration: 1500
                    });
                    isSendMessageDialogOpen = false
                    message = ''
                })">
                    <Loader2 v-if="isSendingMessage" class="w-4 h-4 mr-2 animate-spin" />
                    Enviar mensagem
                </Button>
            </DialogContent>
        </Dialog>

        <!-- Edit Profile Modal -->
        <Dialog v-if="props.user && userAuthenticated.userId === props.user.userId" v-model:open="isEditModalOpen">
            <DialogScrollContent class="w-full max-w-[800px] grid-rows-[auto_minmax(0,1fr)_auto]">
                <DialogHeader>
                    <DialogTitle>Editar perfil</DialogTitle>
                    <DialogDescription>
                        Atualize as informações do seu perfil, dados bancários, senha, e
                        preferências de notificação.
                    </DialogDescription>
                </DialogHeader>
                <Tabs default-value="profile" class="w-full">
                    <TabsList class="grid w-full grid-cols-3">
                        <TabsTrigger value="profile">Perfil</TabsTrigger>
                        <TabsTrigger value="bank">Dados bancários</TabsTrigger>
                        <TabsTrigger value="password">Senha</TabsTrigger>
                    </TabsList>
                    <TabsContent value="profile">
                        <form @submit.prevent="updateProfile">
                            <div class="space-y-4">
                                <div class="space-y-2">
                                    <Label for="name">Nome</Label>
                                    <Input id="name" v-model="profilePayload.name" required />
                                </div>
                                <div class="space-y-2">
                                    <Label for="phoneNumber">Número de telefone</Label>
                                    <Input v-maska="'(##) # ####-####'" type="tel" id="phoneNumber"
                                        v-model="profilePayload.phoneNumber" required />
                                </div>
                                <div class="space-y-2">
                                    <Label for="description">Descrição</Label>
                                    <Textarea id="description" v-model="profilePayload.description" />
                                </div>
                                <Button @click="updateProfile" type="submit" :disabled="!profilePayload.name ||
                                    !profilePayload.description ||
                                    (!!profilePayload.phoneNumber && profilePayload.phoneNumber?.length < 16)">
                                    <Loader2 v-if="isUpdatingProfile" class="w-4 h-4 mr-2 animate-spin" />
                                    Salvar
                                </Button>
                            </div>
                        </form>
                    </TabsContent>
                    <TabsContent value="bank">
                        <ProfileBankAccounts />
                    </TabsContent>
                    <TabsContent value="password">
                        <form @submit.prevent>
                            <div class="space-y-4">
                                <div class="space-y-2">
                                    <Label for="current-password">Senha atual</Label>
                                    <Input id="current-password" type="password"
                                        v-model="changePasswordPayload.passwordOld" required />
                                </div>
                                <div class="space-y-2">
                                    <div>
                                        <Label for="new-password">Nova senha</Label>
                                        <p class="text-xs text-zinc-500">Deve conter no mínimo 8 caracteres</p>
                                    </div>
                                    <Input id="new-password" type="password" v-model="changePasswordPayload.password"
                                        required />
                                </div>
                                <div class="space-y-2">
                                    <Label for="confirm-password">Confirmar nova senha</Label>
                                    <Input id="confirm-password" type="password"
                                        v-model="changePasswordPayload.passwordConfirm" required />
                                </div>
                                <Button :disabled="isChangingPassword ||
                                    !changePasswordPayload.passwordOld ||
                                    !changePasswordPayload.password ||
                                    !changePasswordPayload.passwordConfirm ||
                                    changePasswordPayload.password !== changePasswordPayload.passwordConfirm ||
                                    changePasswordPayload.password.length < 8 ||
                                    changePasswordPayload.passwordConfirm.length < 8" @click="changePassword(
                                        changePasswordPayload,
                                        () => {
                                            toast({
                                                title: 'Senha alterada com sucesso',
                                                class: 'bg-green-500 text-white'
                                            })
                                            changePasswordPayload = {
                                                passwordOld: '',
                                                password: '',
                                                passwordConfirm: '',
                                            }
                                        },
                                        () => toast({
                                            title: 'Senha atual incorreta',
                                            description: 'Tente novamente',
                                            variant: 'destructive'
                                        })
                                    )" type="submit">
                                    <Loader2 v-if="isChangingPassword" class="w-4 h-4 mr-2 animate-spin" />
                                    Salvar
                                </Button>
                            </div>
                        </form>
                    </TabsContent>
                </Tabs>
            </DialogScrollContent>
        </Dialog>

        <Card v-if="props.user && isTradersPlatform" class="w-full">
            <PerformanceChart :tournament-daily-scores="props.user.tournamentDailyScores" />
        </Card>

        <Card class="w-full">
            <!-- Tabs -->
            <Tabs default-value="posts" class="w-full">
                <TabsContent value="posts">
                    <div v-if="isOwnProfile" class="">
                        <CardHeader>
                            <CardTitle class="text-[1.0rem]">Criar Publicação</CardTitle>
                        </CardHeader>
                        <CardContent class="grid gap-6">
                            <Textarea v-model="topicPayload.content" placeholder="No que você está pensando?" />
                        </CardContent>
                        <CardFooter class="flex justify-between">
                            <Button :disabled="!topicPayload.content || isCreatingTopic" @click="createTopic">
                                <Loader2 v-if="isCreatingTopic" class="w-4 h-4 mr-2 animate-spin" />
                                Publicar
                            </Button>
                        </CardFooter>
                    </div>
                    <div v-if="topics && topics.data.length > 0 && props.user" class="p-4 space-y-4">
                        <ProfileTopicComponent v-for="topic in topics.data" :profile="props.user" :key="topic.id"
                            :topic="topic" @delete="topics.data = topics.data.filter((t) => t.id !== topic.id)" />
                        <PaginationApi :data="topics.pagination" :paginate="getTopics" />
                    </div>
                    <div v-else-if="isFetchingTopics" class="p-4 space-y-6 animate-pulse">
                        <div v-for="i in 2" :key="i" class="space-y-3 bg-zinc-900/50 p-4 rounded-lg">
                            <div class="flex items-center space-x-3">
                                <div class="h-10 w-10 bg-zinc-800 rounded-full"></div>
                                <div class="h-4 w-32 bg-zinc-800 rounded"></div>
                            </div>
                            <div class="h-16 w-full bg-zinc-800 rounded"></div>
                        </div>
                    </div>
                    <div v-else-if="!isFetchingTopics && (!topics || topics.data.length === 0)"
                        class="py-20 flex flex-col items-center justify-center text-center">
                        <p class="text-gray-400 text-lg">Nenhuma publicação ainda.</p>
                    </div>
                </TabsContent>
            </Tabs>
        </Card>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import {
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    CardFooter,
} from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogScrollContent,
} from "@/components/ui/dialog";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import {
    Pencil,
    ImageUp,
    Loader2,
    Handshake,
} from "lucide-vue-next";
import { useToast } from "@/components/ui/toast";
import { Profile } from "@/interfaces/profiles/Profile";
import { ProfileMediaTypeEnum } from "@/enums/profiles/ProfileMediaTypeEnum";
import { useAxios } from "@/composables/useAxios";
import { ProfileTopic } from "@/interfaces/profiles/profileTopics/ProfileTopic";
import { Pagination } from "@/interfaces/Pagination";
import ProfileTopicComponent from "./ProfileTopicComponent.vue";
import PaginationApi from "@/components/ui/pagination/PaginationApi.vue";
import { vMaska } from "maska/vue"
import { useAuth } from "@/composables/useAuth";
import PerformanceChart from "./PerformanceChart.vue";
import { achievementCriteriaTypeIcons } from "@/enums/achievements/AchievementCriteriaTypeEnum";
import { useChat } from "@/composables/useChat";
import ProfileBankAccounts from "./ProfileBankAccounts.vue";
import { useTournament } from "@/composables/useTournament";
import { AchievementTypeEnum } from "@/enums/achievements/AchievementTypeEnum";
import { useTenant } from "@/composables/useTenant";

const { isTradersPlatform } = useTenant()
const { userAuthenticated, changePassword, isChangingPassword } = useAuth()
const emit = defineEmits(['update'])
const props = defineProps<{ user?: Profile, profileId: number | string }>();
const { toast } = useToast();

const isOwnProfile = computed(() => {
    return Number(props.profileId) === Number(userAuthenticated.userId)
})
const { hasActiveParticipation } = useTournament()
const axios = useAxios()
const avatar = computed(() =>
    props.user?.media.find((m) => m.type === ProfileMediaTypeEnum.AVATAR)
);
const banner = computed(() =>
    props.user?.media.find((m) => m.type === ProfileMediaTypeEnum.BANNER)
);
const avatarPreview = ref<string>()
const bannerPreview = ref<string>()
const isEditModalOpen = ref(false);
const isUpdatingProfile = ref(false);
const editForm = ref({
    bank: "",
    agency: "",
    account: "",
    document: "",
    notifications: {
        email: false,
        push: false,
        sms: false,
    },
});
const changePasswordPayload = ref({
    password: '',
    passwordConfirm: '',
    passwordOld: '',
})
const profilePayload = ref<{
    avatar?: File
    banner?: File
    name?: string
    description?: string,
    phoneNumber?: string
}>({
    name: props.user?.name,
    description: props.user?.description,
    phoneNumber: props.user?.phoneNumber,
})
const topics = ref<Pagination<ProfileTopic>>()
const isFetchingTopics = ref(true)
const topicPayload = ref<{ content: string }>({ content: '' })
const isCreatingTopic = ref(false)
const { sendMessage, isSendingMessage } = useChat()
const isSendMessageDialogOpen = ref(false)
const message = ref('')
const achievements = computed(() => props.user?.achievements?.filter(achievement => achievement.type === AchievementTypeEnum.ACHIEVEMENT) || [])
const trophies = computed(() => props.user?.achievements?.filter(achievement => achievement.type === AchievementTypeEnum.TROPHY) || [])

const openEditModal = () => {
    editForm.value = {
        bank: "",
        agency: "",
        account: "",
        document: "",
        notifications: {
            email: false,
            push: false,
            sms: false,
        },
    };
    isEditModalOpen.value = true;
};

function onAvatarChange(e: Event) {
    const file = (e.target as HTMLInputElement).files![0]

    profilePayload.value.avatar = file

    avatarPreview.value = URL.createObjectURL(file)
}

function onBannerChange(e: Event) {
    const file = (e.target as HTMLInputElement).files![0]

    profilePayload.value.banner = file

    bannerPreview.value = URL.createObjectURL(file)
}

function updateProfile() {
    isUpdatingProfile.value = true

    const formData = new FormData()

    for (const key in profilePayload.value) {
        if (profilePayload.value[key as keyof typeof profilePayload.value]) {
            formData.append(key, profilePayload.value[key as keyof typeof profilePayload.value] as any)
        }
    }

    axios.patch(`/profile`, formData)
        .then(() => {
            toast({
                title: 'Perfil atualizado com sucesso',
                class: 'bg-green-500 text-white'
            })
            emit('update')
            isEditModalOpen.value = false;
        })
        .finally(() => isUpdatingProfile.value = false)
}

function createTopic() {
    isCreatingTopic.value = true

    axios.post(`/profile/${props.profileId}/topics`, topicPayload.value)
        .then((res) => {
            getTopics()
            topicPayload.value = { content: '' }
        })
        .finally(() => isCreatingTopic.value = false)
}

function getTopics(page = 1) {
    const id = props.user?.id || props.profileId
    console.log('Fetching topics using ID:', id, '(from user:', props.user?.id, ', from route:', props.profileId, ')');
    if (!id) return;
    isFetchingTopics.value = true
    axios.get(`/profile/${id}/topics`, { params: { page } })
        .then((res) => {
            console.log('Topics API response for ID ' + id + ':', res.data);
            topics.value = res.data
        })
        .catch(err => {
            console.error('Error fetching topics:', err);
        })
        .finally(() => {
            isFetchingTopics.value = false
        })
}

watch(() => props.user, (newUser) => {
    if (newUser) {
        profilePayload.value = {
            name: newUser.name,
            description: newUser.description,
            phoneNumber: newUser.phoneNumber,
        }
        if (!topics.value) getTopics()
    }
}, { immediate: true })
</script>
